import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FileTextOutlined } from '@ant-design/icons'
import { Image } from 'antd'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import { selectProfile } from '@store/profile.reducer'
import { taskTrackerApi } from '../../../../api/taskTracker.api'
import {
  extractTaskDescriptionFileIdsFromHtml,
  isEmptyDescriptionHtml,
  normalizeTaskDescriptionForApi,
  prepareTaskDescriptionHtmlForSave,
  TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_LABEL,
} from '../../../../lib/taskTrackerDescription.utils'
import {
  TaskTrackerBoardCard,
  TaskTrackerOnTaskUpdated,
} from '../../../../store/taskTracker.types'
import TaskTrackerRichTextEditor, {
  TaskTrackerRichTextEditorHandle,
} from '../../../TaskTrackerRichTextEditor/TaskTrackerRichTextEditor'
import TaskTrackerPermissionTooltip from '../../../TaskTrackerPermissionTooltip/TaskTrackerPermissionTooltip'
import classes from './index.module.css'

type TaskDescriptionTabProps = {
  taskId: number | null
  value: string
  canEditTask?: boolean
  taskEditDisabledTooltip?: string | null
  onChange: (value: string) => void
  onTaskUpdated?: TaskTrackerOnTaskUpdated
  onEditingChange?: (isEditing: boolean) => void
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const iconProps = {
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
} as const

const TaskDescriptionTab: FC<TaskDescriptionTabProps> = ({
  taskId,
  value,
  canEditTask = true,
  taskEditDisabledTooltip = null,
  onChange,
  onTaskUpdated,
  onEditingChange,
}) => {
  const addAlert = useAddAlert()
  const profileId = useSelector(selectProfile)?.id ?? null
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const editSnapshotRef = useRef('')
  const syncRequestIdRef = useRef(0)
  const editorRef = useRef<TaskTrackerRichTextEditorHandle>(null)

  const runSilentDescriptionRefetch = useCallback(
    async (targetTaskId: number) => {
      const requestId = syncRequestIdRef.current + 1
      syncRequestIdRef.current = requestId

      try {
        const freshTask = await taskTrackerApi.getTask(targetTaskId)
        if (syncRequestIdRef.current !== requestId) return

        const freshDescription = freshTask.description ?? ''
        onChange(freshDescription)
        editorRef.current?.setHtml(freshDescription)
        editSnapshotRef.current = freshDescription
        onTaskUpdated?.(freshTask, { skipDrawerSync: true })
      } catch {
        // Тихая синхронизация: игнорируем ошибки, чтобы не мигал UI и не было лишних тостов.
      }
    },
    [onChange, onTaskUpdated],
  )

  useEffect(() => {
    onEditingChange?.(isEditing)
  }, [isEditing, onEditingChange])

  useEffect(
    () => () => {
      onEditingChange?.(false)
    },
    [onEditingChange],
  )

  const handleFileReject = useCallback(
    (message: string) => {
      addAlert({
        type: 'error',
        title: 'Файл не добавлен',
        text: message,
      })
    },
    [addAlert],
  )

  const handleEdit = useCallback(() => {
    if (!canEditTask) return
    editSnapshotRef.current = value
    setIsEditing(true)
  }, [canEditTask, value])

  const handleCancel = useCallback(() => {
    const snapshot = editSnapshotRef.current

    onChange(snapshot)
    editorRef.current?.setHtml(snapshot)
    editSnapshotRef.current = ''
    setIsEditing(false)
    if (taskId) {
      void runSilentDescriptionRefetch(taskId)
    }
  }, [onChange, runSilentDescriptionRefetch, taskId])

  const handleSave = useCallback(async () => {
    if (!taskId || saving || !canEditTask) return

    setSaving(true)

    try {
      const rawHtml = editorRef.current?.getHtml() ?? value
      const imageWidths = editorRef.current?.getImageWidths() ?? []
      const containerWidth = editorRef.current?.getEditorContainerWidth() ?? 0
      const pendingAttachmentFiles =
        editorRef.current?.getPendingAttachmentFiles() ?? []
      const preparedHtml = await prepareTaskDescriptionHtmlForSave(
        rawHtml,
        profileId,
        imageWidths,
        containerWidth,
        pendingAttachmentFiles,
        taskId,
      )
      const normalizedDescription = normalizeTaskDescriptionForApi(preparedHtml)
      const fileIds = extractTaskDescriptionFileIdsFromHtml(preparedHtml)
      const updatedTask = await taskTrackerApi.updateTaskDescription(
        taskId,
        normalizedDescription,
        fileIds,
      )
      const nextDescription = updatedTask.description ?? ''

      onChange(nextDescription)
      editorRef.current?.setHtml(nextDescription)
      editorRef.current?.clearPendingAttachmentFiles()
      editSnapshotRef.current = nextDescription
      setIsEditing(false)
      onTaskUpdated?.(updatedTask)
      void runSilentDescriptionRefetch(taskId)

      addAlert({
        title: 'Описание сохранено',
      })
    } catch (error: unknown) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: getErrorMessage(error, 'Не удалось сохранить описание задачи'),
      })
    } finally {
      setSaving(false)
    }
  }, [addAlert, canEditTask, onChange, onTaskUpdated, profileId, runSilentDescriptionRefetch, saving, taskId, value])

  const showEmptyState = useMemo(
    () => !isEditing && isEmptyDescriptionHtml(value),
    [isEditing, value],
  )

  const handleDescriptionImageClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isEditing) return

      const target = event.target
      if (!(target instanceof Element)) return

      const image = target.closest('img')
      const src = image?.getAttribute('src')
      if (!src) return

      setPreviewImageSrc(src)
      setPreviewVisible(true)
    },
    [isEditing],
  )

  return (
    <div className={classes.root}>
      <div className={classes.headerPanel}>
        {!isEditing ? (
          <TaskTrackerPermissionTooltip title={taskEditDisabledTooltip}>
            <Button
              type="button"
              color="secondary"
              onClick={handleEdit}
              disabled={!canEditTask}
            >
              Редактировать
            </Button>
          </TaskTrackerPermissionTooltip>
        ) : (
          <div className={classes.actions}>
            <Button
              type="button"
              onClick={() => void handleSave()}
              isLoading={saving}
              disabled={saving}
            >
              Сохранить
            </Button>
            <Button type="button" color="gray" onClick={handleCancel} disabled={saving}>
              Отмена
            </Button>
          </div>
        )}
      </div>

      <div className={classes.editorArea} onClick={handleDescriptionImageClick}>
        {showEmptyState ? (
          <div className={classes.emptyState}>
            <FileTextOutlined className={classes.emptyStateIcon} {...iconProps} />
            <p className={classes.emptyStateTitle}>Описание пока не добавлено</p>
            <p className={classes.emptyStateText}>
              Нажмите «Редактировать», чтобы описать задачу — текст, списки,
              изображения и файлы до {TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_LABEL}.
            </p>
          </div>
        ) : (
          <div className={classes.editorBlock}>
            <TaskTrackerRichTextEditor
              ref={editorRef}
              value={value}
              onChange={onChange}
              placeholder="Добавьте описание задачи..."
              disabled={!isEditing || saving}
              onFileReject={handleFileReject}
            />
            {isEditing ? (
              <p className={classes.editorHint}>
                Максимальный размер одного изображения или файла —{' '}
                {TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_LABEL}.
              </p>
            ) : null}
          </div>
        )}
      </div>
      {previewImageSrc ? (
        <Image
          src={previewImageSrc}
          alt=""
          preview={{
            visible: previewVisible,
            onVisibleChange: (nextVisible) => {
              setPreviewVisible(nextVisible)
              if (!nextVisible) {
                setPreviewImageSrc(null)
              }
            },
          }}
          style={{ display: 'none' }}
        />
      ) : null}
    </div>
  )
}

export default TaskDescriptionTab
